import { useQuery } from '@tanstack/react-query';
import { fetchPipelineFunnel } from '@/lib/api';
import { STAGE_LABELS, type PipelineStage } from '@/types';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { BarChart3 } from 'lucide-react';

interface PipelineFunnelChartProps {
  startDate?: string;
  endDate?: string;
}

const STAGE_GRADIENT: Record<string, string> = {
  sourced: '#6EE7B7',
  applied: '#34D399',
  recruiter_screen: '#10B981',
  shortlist: '#059669',
  sent_to_client: '#047857',
  interview: '#065F46',
  offer: '#064E3B',
  hired: '#022C22',
  disqualified: '#6B7280',
};

function getStageColor(stage: string): string {
  return STAGE_GRADIENT[stage] || '#10B981';
}

function getStageLabel(stage: string): string {
  return STAGE_LABELS[stage as PipelineStage] || stage;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ payload: { stage: string; count: number; percentage: number } }>;
}

function CustomTooltip({ active, payload }: CustomTooltipProps) {
  if (!active || !payload?.length) return null;
  const data = payload[0].payload;
  return (
    <div className="rounded-lg border border-white/10 bg-bg-elevated px-3 py-2 shadow-xl">
      <p className="text-sm font-medium text-text-primary">{getStageLabel(data.stage)}</p>
      <p className="text-sm text-text-secondary">
        {data.count} candidates ({data.percentage.toFixed(1)}%)
      </p>
    </div>
  );
}

export function PipelineFunnelChart({ startDate, endDate }: PipelineFunnelChartProps) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['reports', 'pipeline-funnel', startDate, endDate],
    queryFn: () => fetchPipelineFunnel(startDate, endDate),
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-emerald-500" />
            Pipeline Funnel
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-8 bg-white/5" style={{ width: `${100 - i * 8}%` }} />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-emerald-500" />
            Pipeline Funnel
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-text-secondary text-sm">Failed to load pipeline data.</p>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-emerald-500" />
            Pipeline Funnel
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-12 text-text-tertiary">
            <BarChart3 className="h-10 w-10 mb-3 opacity-40" />
            <p className="text-sm">No data available for the selected period.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const chartData = data.map((item) => ({
    ...item,
    label: getStageLabel(item.stage),
  }));

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-emerald-500" />
          Pipeline Funnel
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 5, right: 60, left: 20, bottom: 5 }}
          >
            <XAxis
              type="number"
              tick={{ fill: '#9CA3AF', fontSize: 12 }}
              axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
              tickLine={false}
            />
            <YAxis
              type="category"
              dataKey="label"
              tick={{ fill: '#D1D5DB', fontSize: 13 }}
              axisLine={false}
              tickLine={false}
              width={120}
            />
            <Tooltip
              content={<CustomTooltip />}
              cursor={{ fill: 'rgba(255,255,255,0.03)' }}
            />
            <Bar
              dataKey="count"
              radius={[0, 4, 4, 0]}
              barSize={28}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              label={(props: any) => {
                const nx = Number(props.x) || 0;
                const ny = Number(props.y) || 0;
                const nw = Number(props.width) || 0;
                const nh = Number(props.height) || 0;
                return (
                  <text
                    x={nx + nw + 8}
                    y={ny + nh / 2}
                    fill="#D1D5DB"
                    fontSize={12}
                    dominantBaseline="central"
                  >
                    {String(props.value ?? '')}
                  </text>
                );
              }}
            >
              {chartData.map((entry) => (
                <Cell key={entry.stage} fill={getStageColor(entry.stage)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
